import React from 'react';
import PropTypes from 'prop-types';

import { Inline, Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import analytics from '../../../../modules/analytics';

import NormalServiceItem from './NormalServiceItem';
import PromotedServiceItem from './PromotedServiceItem';
import {
  PromotionPropType,
  ServiceGroupPropType,
  ServicePropType,
} from '../ServiceList/ServiceListPropTypes';

import { Stack } from '../../../ui';
import { useServiceInteractionCounter } from '../../../../hooks';
import withHook from '../../../withHook';
import TopServiceRatingBanner from '../HighlightedServicesSwiper/TopServiceRatingBanner';

/**
 * Determines whether or not an object prop value has changed between two given "props" objects.
 * Uses the given `idKey` to determine equality between two existing object values.
 *
 * @param {Object} props The current props hash
 * @param {Object} nextProps The next props hash
 * @param {String} propName The name of the prop to inspect
 * @param {String} idKey The key to use in comparing items of the prop
 *
 * @returns {Boolean} True if the prop has changed, otherwise false
 */
const propHasChanged = (props, nextProps, propName, idKey) => {
  const currentValue = props[propName];
  const nextValue = nextProps[propName];
  let result = false;

  if (!nextValue || !currentValue) {
    if (nextValue !== currentValue) {
      result = true;
    }
  } else if (nextValue[idKey] !== currentValue[idKey]) {
    result = true;
  }

  return result;
};

/**
 * Determines which service item should be rendered, based on the service / promotion that are
 * supplied. This also handles the borders that appear around the whole promoted item group.
 */
class ServiceItem extends React.Component {
  static propTypes = {
    appearance: PropTypes.oneOf([
      'default',
      'notifyMe',
    ]),
    /** True if edit mode is enabled */
    isEditMode: PropTypes.bool,
    /** True if a pro is viewing their own profile */
    isOwnProfile: PropTypes.bool,
    /** The service to book */
    service: ServicePropType.isRequired,
    /** The provider associated with the service */
    providerId: PropTypes.number.isRequired,
    /** The phone number of the pro */
    providerPhone: PropTypes.string,
    /** The promotion associated with the service, if any */
    promotion: PromotionPropType,
    /** The overall index of the service in the list, used for tracking */
    index: PropTypes.number.isRequired,
    /** The service group, used for tracking */
    group: ServiceGroupPropType.isRequired,
    /** True if the item should be disabled, otherwise false */
    isDisabled: PropTypes.bool,
    /** True if the profile supports online booking, otherwise false */
    allowOnlineBooking: PropTypes.bool,
    /** Text summarizing the discount, e.g. "$5 Off" */
    formattedDiscount: PropTypes.string,
    /** True if the pro has smart pricing enabled */
    providerSmartPricingEnabled: PropTypes.bool,
    /** Props for SeeReviews link and modal */
    seeReviewsProps: PropTypes.object,
    /** Force description into a single line */
    useShortDescription: PropTypes.bool,
    renderRight: PropTypes.func,
    /** The background color of the main container */
    backgroundColor: PropTypes.string,
    /** Aggregate interactions in the DB */
    countInteraction: PropTypes.func.isRequired,
    popularServiceRankingMap: PropTypes.object,
  };

  static defaultProps = {
    appearance: 'default',
    isDisabled: false,
    isEditMode: false,
    isOwnProfile: false,
    allowOnlineBooking: false,
    formattedDiscount: null,
    promotion: null,
    providerPhone: null,
    providerSmartPricingEnabled: false,
    seeReviewsProps: null,
    useShortDescription: false,
    renderRight: null,
    popularServiceRankingMap: null,
    backgroundColor: colors.whiteWash,
  };

  state = {
    isExpanded: false,
  };

  shouldComponentUpdate(nextProps, nextState) {
    // overriding this function to make sure identical group / promotion / service components don't
    // get re-rendered

    // remove some props from nextProps because we're comparing them separately
    const {
      service, promotion, group, seeReviewsProps, ...restProps
    } = nextProps;

    if (nextState.isExpanded !== this.state.isExpanded) {
      return true;
    }

    if (propHasChanged(this.props, nextProps, 'service', 'id')) {
      return true;
    }

    if (propHasChanged(this.props, nextProps, 'service', 'idList')) {
      return true;
    }

    if (propHasChanged(this.props, nextProps, 'promotion', 'name')) {
      return true;
    }

    if (propHasChanged(this.props, nextProps, 'group', 'name')) {
      return true;
    }

    if (propHasChanged(this.props, nextProps, 'seeReviewsProps', 'service')) {
      return true;
    }

    return Object.keys(restProps)
      .some(key => restProps[key] !== this.props[key]);
  }

  /**
   * Handle clicks on the info icon
   */
  handleInfoClick = () => {
    const {
      promotion, service, providerId, countInteraction,
    } = this.props;
    const { isExpanded } = this.state;
    countInteraction(providerId, service.id, this.props.isEditMode);

    analytics.track('client_proprofile_serviceinfo_click', {
      service_id: `${promotion ? 'promo-' : ''}${service.id}`,
      default_info: service.isBlurbDefaulted ? 1 : 0,
      is_top_service: service.isTopService,
      is_popular: (service.tags || []).includes('popular'),
      elementClicked: 'Service description',
    });
    this.setState({
      isExpanded: !isExpanded,
    });
  };

  handleImageClick = () => {
    const { promotion, service } = this.props;

    analytics.track('client_proprofile_serviceimage_click', {
      service_id: `${promotion ? 'promo-' : ''}${service.id}`,
    });
  };

  render() {
    const {
      service,
      promotion,
      providerSmartPricingEnabled,
      appearance,
      renderRight,
      backgroundColor,
      popularServiceRankingMap,
    } = this.props;
    const { isTopService } = service;
    const { isExpanded } = this.state;
    const serviceItemProps = {
      ...this.props,
      isExpanded,
      showAndUp: service.cost_and_up || providerSmartPricingEnabled,
      description: promotion ? promotion.description : service.blurb,
      onInfoClick: this.handleInfoClick,
      onImagePress: this.handleImageClick,
    };
    let ServiceItemContent = NormalServiceItem;

    if (promotion) {
      ServiceItemContent = PromotedServiceItem;
    }

    // COM-776
    const popularServiceRankingNumber = popularServiceRankingMap?.[service.id];
    return (
      <Inline
        flexGrow={1}
        flexShrink={1}
        color={backgroundColor}
        padding={16}
        minHeight={isTopService ? 135 : undefined}
      >
        <Stack
          flexGrow={1}
          flexShrink={1}
          space={popularServiceRankingNumber ? 20 : null}
        >
          {popularServiceRankingNumber && (
            <Box maxWidth={108} maxHeight={20}>
              <TopServiceRatingBanner
                serviceRanking={popularServiceRankingNumber}
              />
            </Box>
          )}
          <ServiceItemContent
            appearance={appearance}
            additionalTracking={{
              is_popular: (service.tags || []).includes('popular'),
            }}
            {...serviceItemProps}
          />
        </Stack>
        {renderRight && renderRight(service.id)}
      </Inline>
    );
  }
}

export default withHook(ServiceItem, 'countInteraction', useServiceInteractionCounter);
